import type { FontSpec, GlyphAtlas, GlyphFace } from "./GlyphAtlas.js";

export interface TextMetrics {
  /** Logical pixels. */
  width: number;
  ascent: number;
  descent: number;
  height: number;
}

/**
 * Raster sizes are quantized so that panning or smoothly zooming a
 * ScrollViewer reuses a handful of atlas entries instead of rasterizing a new
 * set of glyphs on every frame.
 */
export function quantizeRasterSize(logicalSize: number, rasterScale: number): number {
  const px = logicalSize * rasterScale;
  if (px <= 32) return Math.max(1, Math.round(px * 2) / 2); // 0.5px steps when small
  if (px <= 96) return Math.round(px);
  return Math.round(px / 2) * 2;
}

/**
 * Measures and positions text.
 *
 * v1 is LTR, single-line, and advances per code point, so kerning pairs and
 * complex shaping (Arabic, Devanagari) are not honored. Measurement uses the
 * same per-character advances the draw path uses, so a measured width always
 * matches what gets painted.
 *
 * **A measured width is the same number at every scale, and that is the whole contract.**
 * `quantizeRasterSize` rounds the raster onto the pixel grid - that is its job, and it is what
 * makes glyphs crisp - so the advances that come back describe a font of `rasterPx` rather than
 * the one that was asked for. Converting them by `font.size / rasterPx` undoes exactly that
 * rounding and lands back on the requested size, which is why measuring a string at 0.25x, at
 * 1x and at 4x gives one answer to the last bit. Divide by the raster scale instead and the
 * answer moves by up to 8% across that range: the same label would wrap in different places
 * depending on the zoom it was being *viewed* at, and in the designer a line of text would
 * overflow its box at one zoom and not another.
 *
 * `RenderContext.drawText` converts the same way, so what is painted is what was measured. It
 * has to do that deliberately - the pen wants the advances scaled back to the requested size,
 * not the raster's own - and getting it wrong is a fraction of a pixel per glyph that
 * accumulates along the line: at dpr 1.5 a 12.5px font rasterizes at 19 rather than 18.75, and
 * by column 80 that is a whole character. The caret sat one glyph left of the character it
 * belonged after, while backspace - which works off the index, not the measurement - deleted
 * from the true end.
 */
export class TextLayout {
  #atlas: GlyphAtlas;
  /**
   * Whole-string advances, per face so only the text itself is hashed. Two
   * generations rather than one bounded map: clearing a full map outright
   * dropped every hot string along with the cold ones, and a busy UI re-measured
   * everything from nothing each time it filled.
   */
  #widths = new WeakMap<GlyphFace, { current: Map<string, number>; previous: Map<string, number> }>();

  constructor(atlas: GlyphAtlas) {
    this.#atlas = atlas;
  }

  get atlas(): GlyphAtlas {
    return this.#atlas;
  }

  /** Total advance of `text` in device pixels at `rasterPx`. */
  advanceOf(text: string, font: FontSpec, rasterPx: number): number {
    return this.#advance(this.#atlas.face(font, rasterPx), text);
  }

  #advance(face: GlyphFace, text: string): number {
    let cache = this.#widths.get(face);
    if (!cache) this.#widths.set(face, (cache = { current: new Map(), previous: new Map() }));
    let total = cache.current.get(text);
    if (total !== undefined) return total;
    total = cache.previous.get(text) ?? sumAdvances(this.#atlas, face, text);
    if (cache.current.size >= WIDTH_GENERATION) {
      cache.previous = cache.current;
      cache.current = new Map();
    }
    cache.current.set(text, total);
    return total;
  }

  /**
   * Measure in logical pixels at `rasterScale` (normally the device pixel
   * ratio). Layout code should measure at scale 1 so results are zoom-independent.
   */
  measure(text: string, font: FontSpec, rasterScale: number): TextMetrics {
    const rasterPx = quantizeRasterSize(font.size, rasterScale);
    const face = this.#atlas.face(font, rasterPx);
    const m = face.metrics;
    const inv = font.size / rasterPx; // device px at rasterPx -> logical px
    return {
      width: this.#advance(face, text) * inv,
      ascent: m.ascent * inv,
      descent: m.descent * inv,
      height: m.lineHeight * inv,
    };
  }

  /** `measure(...).width` without the metrics object, for callers that only want a width. */
  measureWidth(text: string, font: FontSpec, rasterScale: number): number {
    const rasterPx = quantizeRasterSize(font.size, rasterScale);
    return this.#advance(this.#atlas.face(font, rasterPx), text) * (font.size / rasterPx);
  }

  /**
   * Index of the character boundary nearest `xLogical` from the string start —
   * used for caret placement and mouse hit-testing in TextBox.
   */
  indexAtX(text: string, font: FontSpec, rasterScale: number, xLogical: number): number {
    const rasterPx = quantizeRasterSize(font.size, rasterScale);
    const face = this.#atlas.face(font, rasterPx);
    const inv = font.size / rasterPx;
    let pen = 0;
    for (let i = 0; i < text.length; ) {
      const code = codePointAt(text, i);
      const adv = this.#atlas.glyphOf(face, code).advance * inv;
      if (xLogical < pen + adv / 2) return i;
      pen += adv;
      i += code > 0xffff ? 2 : 1;
    }
    return text.length;
  }

  /** Logical x offset of the character boundary at `index`. */
  xAtIndex(text: string, font: FontSpec, rasterScale: number, index: number): number {
    return this.measureWidth(text.slice(0, index), font, rasterScale);
  }

  /**
   * Truncate to fit `maxWidth` logical px, appending an ellipsis when clipped.
   *
   * One pass over the advances. It used to bisect, joining and measuring a
   * fresh prefix at every step, and every one of those prefixes was then kept
   * in the width cache - for every truncated cell, on every frame.
   */
  ellipsize(text: string, font: FontSpec, rasterScale: number, maxWidth: number): string {
    const rasterPx = quantizeRasterSize(font.size, rasterScale);
    const face = this.#atlas.face(font, rasterPx);
    const inv = font.size / rasterPx;
    if (this.#advance(face, text) * inv <= maxWidth) return text;
    const ellipsis = "…";
    const ellipsisWidth = this.#advance(face, ellipsis) * inv;
    if (ellipsisWidth > maxWidth) return "";
    // Summed left to right from zero, as `measure` sums a prefix, so the cut
    // lands exactly where bisecting over measured prefixes put it.
    let total = 0;
    let cut = 0;
    for (let i = 0; i < text.length; ) {
      const code = codePointAt(text, i);
      total += this.#atlas.glyphOf(face, code).advance;
      if (total * inv + ellipsisWidth > maxWidth) break;
      i += code > 0xffff ? 2 : 1;
      cut = i;
    }
    return text.slice(0, cut) + ellipsis;
  }

  clearCache(): void {
    this.#widths = new WeakMap();
  }
}

/** Strings kept per face before the older generation is dropped. */
const WIDTH_GENERATION = 4096;

/**
 * The code point at `i`, reading a surrogate pair whole. A lone surrogate
 * comes back as itself, which is what `for...of` over a string yields too.
 */
export function codePointAt(text: string, i: number): number {
  const hi = text.charCodeAt(i);
  if (hi >= 0xd800 && hi <= 0xdbff && i + 1 < text.length) {
    const lo = text.charCodeAt(i + 1);
    if (lo >= 0xdc00 && lo <= 0xdfff) return ((hi - 0xd800) << 10) + (lo - 0xdc00) + 0x10000;
  }
  return hi;
}

function sumAdvances(atlas: GlyphAtlas, face: GlyphFace, text: string): number {
  let total = 0;
  for (let i = 0; i < text.length; ) {
    const code = codePointAt(text, i);
    total += atlas.glyphOf(face, code).advance;
    i += code > 0xffff ? 2 : 1;
  }
  return total;
}

/**
 * Break `text` into lines no wider than `maxWidth`.
 *
 * Pure, and takes its own `measure`, so the rule can be unit-tested without a
 * GPU and reused by anything that has a way to measure a string.
 *
 * Explicit newlines always break, and a blank line between paragraphs
 * survives as an empty string so it still occupies a line. Words are kept
 * whole where they fit; one too long for the full width is broken across
 * lines rather than left to overhang, which is what a URL or an unspaced run
 * of CJK would otherwise do.
 */
export function wrapLines(text: string, maxWidth: number, measure: (s: string) => number): string[] {
  // Each word is measured once and a line's width kept as a running sum.
  // Measuring every candidate line whole made a paragraph quadratic in its
  // length, and filled the width cache with prefixes nobody asked for again.
  let spaceWidth = -1;
  const wrapped: string[] = [];
  const widths: number[] = [];

  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      wrapped.push("");
      widths.push(0);
      continue;
    }
    let line = "";
    let lineWidth = 0;
    for (const word of words) {
      const w = measure(word);
      if (!line) {
        line = word;
        lineWidth = w;
        continue;
      }
      if (spaceWidth < 0) spaceWidth = measure(" ");
      const candidate = lineWidth + spaceWidth + w;
      if (candidate <= maxWidth) {
        line = `${line} ${word}`;
        lineWidth = candidate;
        continue;
      }
      wrapped.push(line);
      widths.push(lineWidth);
      line = word;
      lineWidth = w;
    }
    wrapped.push(line);
    widths.push(lineWidth);
  }

  if (maxWidth <= 0) return wrapped;

  // Second pass: hard-break whatever a single long word made too wide. Split
  // by code point, so a surrogate pair is never cut in half.
  const out: string[] = [];
  for (let n = 0; n < wrapped.length; n++) {
    const line = wrapped[n]!;
    if (widths[n]! <= maxWidth) {
      out.push(line);
      continue;
    }
    let current = "";
    let currentWidth = 0;
    for (const ch of line) {
      const w = measure(ch);
      if (current && currentWidth + w > maxWidth) {
        out.push(current);
        current = "";
        currentWidth = 0;
      }
      current += ch;
      currentWidth += w;
    }
    out.push(current);
  }
  return out;
}

/**
 * Break offsets for wrapping `text[start..end)` to `maxWidth`, without
 * altering a character of it.
 *
 * `wrapLines` is for display: it collapses runs of whitespace and hands back
 * new strings. An editor cannot use that — every caret index has to keep
 * pointing at the same character — so this returns the offsets to break at
 * and leaves the text alone. Breaks are placed after the whitespace that
 * caused them, so a trailing space stays on the line it belongs to, the way
 * every text editor does it.
 *
 * The returned array always starts with `start` and never includes `end`.
 */
export function wrapLineOffsets(
  text: string,
  start: number,
  end: number,
  maxWidth: number,
  measure: (from: number, to: number) => number,
): number[] {
  const breaks = [start];
  if (maxWidth <= 0 || end <= start) return breaks;

  let lineStart = start;
  // Last point on this line the text could be broken at, and where the run
  // that follows it begins.
  let candidate = -1;
  let i = start;

  while (i < end) {
    // Consume one chunk: a run of non-space, then the spaces trailing it.
    const chunkStart = i;
    while (i < end && !isSpace(text.charCodeAt(i))) i++;
    const wordEnd = i;
    while (i < end && isSpace(text.charCodeAt(i)) && text.charCodeAt(i) !== 10) i++;

    if (measure(lineStart, i) <= maxWidth || chunkStart === lineStart) {
      candidate = i;
      // A single chunk too wide for an empty line has to be split mid-word,
      // or it would overhang for ever.
      if (chunkStart === lineStart && measure(lineStart, wordEnd) > maxWidth) {
        let cut = lineStart;
        for (let j = stepForward(text, lineStart); j <= wordEnd; j = stepForward(text, j)) {
          if (measure(lineStart, j) > maxWidth) break;
          cut = j;
        }
        if (cut > lineStart && cut < wordEnd) {
          breaks.push(cut);
          lineStart = cut;
          candidate = -1;
          i = cut;
          continue;
        }
      }
      continue;
    }

    // The chunk does not fit: break before it and start the line again there.
    const at = candidate > lineStart ? candidate : chunkStart;
    breaks.push(at);
    lineStart = at;
    candidate = -1;
    i = at;
  }
  return breaks;
}

/** What `/\s/` matches, without building a one-character string to test. */
function isSpace(c: number): boolean {
  return (
    (c >= 9 && c <= 13) ||
    c === 32 ||
    c === 0xa0 ||
    c === 0x1680 ||
    (c >= 0x2000 && c <= 0x200a) ||
    c === 0x2028 ||
    c === 0x2029 ||
    c === 0x202f ||
    c === 0x205f ||
    c === 0x3000 ||
    c === 0xfeff
  );
}

/** One code point forward, so a surrogate pair is never cut in half. */
function stepForward(text: string, index: number): number {
  const code = text.codePointAt(index);
  return index + (code !== undefined && code > 0xffff ? 2 : 1);
}


/**
 * Stands in for an atomic span — an inline icon or key cap — while wrapping.
 * U+FFFC is the character the Unicode standard reserves for exactly this: it
 * takes part in line breaking as one unbreakable unit and never matches \s.
 */
const OBJECT_REPLACEMENT = "\ufffc";

/** All `wrapSpanRuns` needs to know about a span. */
export interface SpanLike {
  readonly text: string;
}

/**
 * Wrap a run of styled spans to `maxWidth`, one array of spans per line.
 *
 * Rather than write a second wrapping rule, this flattens the spans into a
 * single string with an owner index per character, runs the existing
 * `wrapLineOffsets` over that, and cuts the result back into spans. So styled
 * text breaks exactly where plain text would, including the hard-break of a
 * word too long for the width — and there is one rule to fix if it is wrong.
 *
 * `measure` and `slice` take a character range within one span's own text.
 * A span whose `text` is empty is atomic: it wraps as a single unit, is never
 * split, and its `slice` is never called.
 */
export function wrapSpanRuns<T extends SpanLike>(
  spans: readonly T[],
  maxWidth: number,
  measure: (span: T, from: number, to: number) => number,
  slice: (span: T, from: number, to: number) => T,
): T[][] {
  if (spans.length === 0) return [[]];

  let flat = "";
  const owner: number[] = [];
  const local: number[] = [];
  spans.forEach((span, i) => {
    const atomic = span.text.length === 0;
    const text = atomic ? OBJECT_REPLACEMENT : span.text;
    for (let j = 0; j < text.length; j++) {
      owner.push(i);
      local.push(atomic ? 0 : j);
    }
    flat += text;
  });

  // Measure a flat range by summing the per-span pieces it covers.
  const measureFlat = (from: number, to: number): number => {
    let total = 0;
    let i = from;
    while (i < to) {
      const which = owner[i]!;
      let j = i;
      while (j < to && owner[j] === which) j++;
      const span = spans[which]!;
      total += span.text.length === 0 ? measure(span, 0, 0) : measure(span, local[i]!, local[j - 1]! + 1);
      i = j;
    }
    return total;
  };

  // Explicit newlines always break, and `wrapLineOffsets` does not handle
  // them, so paragraphs are split off first and wrapped one at a time.
  const breaks: number[] = [];
  let paragraphStart = 0;
  for (let i = 0; i <= flat.length; i++) {
    if (i < flat.length && flat[i] !== "\n") continue;
    breaks.push(...wrapLineOffsets(flat, paragraphStart, i, maxWidth, measureFlat));
    paragraphStart = i + 1;
  }

  const lines: T[][] = [];
  for (let b = 0; b < breaks.length; b++) {
    const start = breaks[b]!;
    // Up to the next break, or the end of this paragraph, whichever is first.
    let end = b + 1 < breaks.length ? breaks[b + 1]! : flat.length;
    const newline = flat.indexOf("\n", start);
    if (newline >= 0 && newline < end) end = newline;

    const runs: T[] = [];
    let i = start;
    while (i < end) {
      const which = owner[i]!;
      let j = i;
      while (j < end && owner[j] === which) j++;
      const span = spans[which]!;
      runs.push(span.text.length === 0 ? span : slice(span, local[i]!, local[j - 1]! + 1));
      i = j;
    }
    lines.push(runs);
  }
  return lines;
}
